import {
    EzBadParamException,
    EzSerializationException
} from '/ezlibrary/exceptions/EzExceptions.js';

import {
    EzObject,
    EzBoolean,
    EzNumber,
    EzString,
    EzJson,
    EzFunction
} from '/ezlibrary/helpers/EzHelpers.js';

/*
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ENGINEERING NOTES
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    Classes that extend EzJSONSerializable MUST override and implement the JSON
    serialization methods and properties by calling the super property or
    method to make sure the property this context is pass along.

    In addition, you should override and implement the ezJSONProps getter
    and return an array of property names that should serialize to JSON.
    Not implementing the ezJSONProps could result in an exception
    (if the auto-serialize fails) and will then require you to
    override and implement the full asJSONObject getter for the class.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
*/

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Recommended Implementation Template
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//      /**
//       * @override
//       * Overrides getter asJSONObject() from class EzJSONSerializable
//       * @public @readonly @property
//       * Gets this instance as a JSON Object (will no longer have the getter/setter methods).
//       * Override this method provide the JSON object that will then be used to serialize
//       * your instance to JSON by the other methods.
//       * If you have getters or setters in your class you MUST override this method in order
//       * to provide the correct JSON object for serialization. If you do not have any getters or setters
//       * in your class, then you do not need to override this method as the default implementation will work just fine.
//       * @returns {object}
//       */
//      get asJSONObject() {
//          return super.asJSONObject;
//      }
//
//      /**
//       * @override
//       * Overrides function ezFromJSONObject(jsonObject) from class EzJSONSerializable
//       * @public @function
//       * Copies properties from the provided jsonObject to this instance (even if this instance does not define the property)
//       * Override this method to transform the provided jsonObject to your class instance getters and setters.
//       * @param {Object} jsonObject
//       * @returns {Object}
//       * Returns this instance with the key & values from the provided jsonObject.
//       */
//      ezFromJSONObject(jsonObject) {
//          return super.ezFromJSONObject(jsonObject);
//      }
//
//      /**
//       * @override
//       * Overrides getter asJSON() from class EzJSONSerializable
//       * @public @readonly @property
//       * Gets this instance as JSON string (no formatting)
//       * @returns {String}
//       */
//      get asJSON() {
//          return super.asJSON;
//      }
//
//      /**
//       * @override
//       * Overrides function ezToJSON(indentValue, htmlDisplay) from class EzJSONSerializable
//       * @public @function
//       * Converts this instance into a JSON string with optional formatting.
//       * @param {undefined|null|Number} indentValue
//       * @param {undefined|null|Boolean} htmlDisplay
//       * @returns {String}
//       */
//      ezToJSON(indentValue, htmlDisplay) {
//          return super.ezToJSON(indentValue, htmlDisplay);
//      }
//
//      /**
//       * @override
//       * Overrides function ezLoadFromJson(jsonString) from class EzJSONSerializable
//       * @public @function
//       * Converts the provided jsonString to a JSON object and then
//       * passes that object to ezFromJSONObject() to copies properties to this instance
//       * (even if this instance does not define the property)
//       * @param {String} jsonString
//       * @returns {Object}
//       * Returns this instance with the key & values from the provided json string.
//       */
//      ezLoadFromJson(jsonString) {
//          return super.ezLoadFromJson(jsonString);
//      }
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

/**
 * @class
 * @description
 * Provides JSON serialization methods and properties.
 * See the recommended implementation template above for example of how to
 * implement/override the methods and properties in this class.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Provides the following static functions:`
 * 1) ezLoadFromJSONObject
 *    Assings the values of the properties from loadFromJsonObject (2nd param) that match properties in loadToJsonObject.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Provides the following class instance functions and properties:
 * 1) toJSON()
 *    Allows JSON.stringify the ability to transform the class to JSON after the simple JSON Object is created.
 *    JSON.stringify will call the toJSON() method if it is available for the object.
 * 2) get asJSONObject()
 *    Public read-only property that returns this instance as a JSON Object (will no longer have the getter/setter methods).
 * 3) get asJSON()
 *    Public read-only property that returns this instance as JSON string (no formatting)
 * 4) ezFromJSONObject(jsonObject)
 *    Public function that copies properties from the provided jsonObject to this instance (even if this instance does not define the property)
 * 5) ezToJSON(indentValue, htmlDisplay)
 *    Publi cfunction that converts this instance into a JSON string with optional formatting.
 * 7) ezLoadFromJson(jsonString)
 *    public function that converts the provided jsonString to a JSON object and then passes that object to ezFromJSONObject() to copies properties to
 *    this instance (even if this instance does not define the property)
 * 7) ezLogError(errorMessage = EzString.EMPTY, err = null)
 *    Logs the err and errorMessage base upon one the following:
 *      a) Attempts to log the provided err and errorMessage using EzClocker's EzLogger singleton instance.
 *      b) If the EzClocker EzLogger instance is not available, then attempts to write the login using the globalThis.console logger.
 *      c) If the EzClocker EzLogger instance is not available and then globalThis.console logger is not available, then the logging attempt is ignored.
 * 8) ezLogWarning(warningMessage = EzString.EMPTY)
 *    Logs the err and errorMessage base upon one the following:
 *      a) Attempts to log the provided err and errorMessage using EzClocker's EzLogger singleton instance.
 *      b) If the EzClocker EzLogger instance is not available, then attempts to write the login using the globalThis.console logger.
 *      c) If the EzClocker EzLogger instance is not available and then globalThis.console logger is not available, then the logging attempt is ignored.
 * 9) ezTransformThisToJSONObject()
 *    Attemnpts to transform the class instance to a raw JSON object while also attempting to interpret getter/setter methods
 *    that the normal JSON.serialize(...) method call would otherwise miss.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzJSONSerializable } from '/ezlibrary/entities/EzJSONSerializable.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzJSONSerializable {
    /**
     * @static
     * @public @function
     * Assings the values of the properties from loadFromJsonObject (2nd param)
     * that match properties in loadToJsonObject.
     * 1) If the loadFromJsonObject and loadToJsonObject is undefined or null then
     *    an empty object is returned.
     * 2) If the loadFromJsonObject is undefined or null, then the loadToJsonObject is
     *    returned.
     * 3) If the loadToJsonObject is undefined or null then a copy of loadFromJsonObject
     *    is created and returned.
     * @param {undefined|null|object} loadFromJsonObject
     * @param {undefined|null|object} loadToJsonObject
     * @returns {object}
     */
    static ezLoadFromJSONObject(loadFromJsonObject, loadToJsonObject) {
        if (!EzObject.isValid(loadToJsonObject)) {
            loadToJsonObject = EzObject.EMPTY;

            if (!EzObject.isValid(loadFromJsonObject)) {
                return loadToJsonObject;
            }

            return {
                ...loadFromJsonObject
            };
        }

        if (!EzObject.isValid(loadFromJsonObject)) {
            return loadToJsonObject;
        }

        for (const propertyName in loadFromJsonObject) {
            if (EzObject.hasProperty(loadToJsonObject, propertyName)) {
                loadToJsonObject[propertyName] = loadFromJsonObject[propertyName];
            }
        }

        return loadToJsonObject;
    }

    /**
     * @public @readonly @property
     * Gets this instance as JSON string (no formatting)
     * @returns {String}
     */
    get asJSON() {
        return this.ezToJSON(EzNumber.THREE, false);
    }

    /**
     * @public @readonly @property
     * Gets this instance as a JSON Object (will no longer have the getter/setter methods).
     * Useful for serailization to JSON.
     * @returns {object}
     * Returns this instance serialized to a JSON object.
     * If unable to transform this instance to a JSON object the following error JSON object is returned instead:
     * {
     *      serializationError: EzString.stringWithTextOrDefault(
     *          err?.message,
     *          'Unknown error during JSON serialization.'),
     *
     *      toString: this.toString()
     *  }
     */
    get asJSONObject() {
        try {
            return this.ezTransformThisToJSONObject();
        } catch (err) {
            const em = EzString.em`
                Unable to automaticlly generate the simple JSON object of class ${this.constructor.name}
                due to the following error ${err.message}.`;

            const msg = EzString.em`
                ${em}
                You possibly need to override instance method asJSONObject() and provide your own custom implementation.
                In order to always return a valid JSON object from asJSONObject() function calls, the following object
                is returned with information about the error:
                {
                    serializationError: EzString.stringWithTextOrDefault(
                        err?.message,
                        'Unknown error during JSON serialization.'),
                    // Return this instance using the native toString() instead
                    toString: this.toString()
                }`;

            this.ezLogWarning(msg);

            return EzJson.toJson({
                serializationError: EzString.stringWithTextOrDefault(
                    err?.message,
                    'Unknown error during JSON serialization.'),
                toString: this.toString()
            });
        }
    }

    /**
     * @public @function
     * Converts this instance into a JSON string with optional formatting.
     * @param {undefined|null|number} indentValue
     * Default: 3
     * @param {undefined|null|boolean} htmlDisplay
     * Default: false
     * @returns {string}
     */
    ezToJSON(indentValue = 3, htmlDisplay = false) {
        try {
            return EzJson.toJson(
                this.asJSONObject,
                EzNumber.numberOrNull(indentValue),
                EzBoolean.booleanOrFalse(htmlDisplay));
        } catch (err) {
            const wm = EzString.msg`
                Failed to transform instance of ${this.constructor.name} to a JSON string with formatting
                indentValue=3 ${EzBoolean.booleanOrFalse(htmlDisplay) ? ' for display in HTML ' : EzString.EMPTY}
                due to the following error: ${err.message}.
                Will fallback to returning the value as the following JSON object: { serializationError: 'string', toString: this.toString() }`;

            this.ezLogWarning(wm);

            return EzJson.toJson({
                serializationError: wm,
                toString: this.toString()
            });
        }
    }

    /**
     * @public @function
     * Copies properties from the provided jsonObject to this instance (even if this instance does not define the property)
     * @param {object} jsonObject
     * @returns {object}
     * Returns this instance with the key & values from the provided jsonObject.
     */
    ezFromJSONObject(jsonObject) {
        if (!EzObject.isValid(jsonObject)) {
            throw new EzBadParamException(
                'jsonObject',
                this,
                this.ezFromJSONObject);
        }

        try {
            for (const propertyName in jsonObject) {
                // Assigning the properties from the jsonObject to this instance
                this[propertyName] = jsonObject[propertyName];
            }
        } catch (err) {
            const em = EzString.em`
                Failed to transform instance of ${this.constructor.name} to a raw JSON Object due to the following error:
                Error: ${err.message}`;

            this.ezLogCaughtException(err, em);

            throw new EzSerializationException(
                em,
                err);
        }

        return this;
    }

    /**
     * @public @function
     * Converts the provided jsonString to a JSON object and then
     * passes that object to ezFromJSONObject() to copies properties to this instance
     * (even if this instance does not define the property)
     * @param {string} jsonString
     * @returns {object}
     * Returns this instance with the key & values from the provided json string.
     */
    ezLoadFromJson(jsonString) {
        if (!EzString.stringHasLength(jsonString)) {
            throw new EzBadParamException(
                'jsonString',
                this,
                this.ezLoadFromJson);
        }

        return this.ezLoadFromJSONObject(EzJson.fromJson(jsonString));
    }

    /**
     * @public @function
     * Allows JSON.stringify the ability to transform the class to JSON after the simple JSON Object is created.
     * JSON.stringify will call the toJSON() method if it is available for the object.
     * @returns {string}
     */
    toJSON() {
        return EzJson.toJson(this);
    }

    /**
     * @public @readonly @property
     * Attemnpts to transform the class instance to a raw JSON object while also attempting to interpret getter/setter methods
     * that the normal JSON.serialize(...) method call would otherwise miss.
     * @returns {object}
     */
    ezTransformThisToJSONObject() {
        const jsonObject = {
            ...this
        };

        try {
            // Experimental logic to transform get/set properties to basic public fields on the jsonObject.
            // Info: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/getPrototypeOf
            // Info: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/getOwnPropertyDescriptors
            let propertyDescriptors = Object.getOwnPropertyDescriptors(Object.getPrototypeOf(this));

            propertyDescriptors = Object.entries(propertyDescriptors)
                .filter(
                    // Filter only the getter property descriptors
                    ([key, descriptor]) => EzString.hasLength(key) &&
                        EzFunction.isFunction(descriptor?.get))
                .map(
                    ([key, descriptor]) => {
                        // Key = Getter/property name
                        // Valid description with valid key value
                        const validGetterProperty = EzObject.isValid(descriptor) && EzString.hasLength(key) &&
                            0 !== EzNumber.numberOrZero(key?.length) &&
                            '_' !== key?.[0] &&
                            key !== 'triggerOwner';

                        if (validGetterProperty) {
                            // Ignoring null/undefined descriptors, null/undefined and empty key values, and any key that
                            // starts with the '_'.
                            try {
                                jsonObject[key] = EzObject.assignOrNull(this?.[key]);
                            } catch (err) {
                                this.ezLogError(
                                    EzString.em`
                                        Unexpected error while auto-transforming object ${this.constructor.name} to a simple JSON object:
                                        Tranformation of getter ${key} failed with error ${err.message}. Will ignore the property and
                                        continue with auto-transforming.`);
                            }
                        }

                        return descriptor;
                    });

            globalThis?.console?.logger.debug(`Filtered property descriptors: ${EzJson.toJson(propertyDescriptors)}`);
        } catch (err) {
            this.ezLogError(
                EzString.em`
                    Failed to fully auto-transform object ${this.constructor.name} to a simple JSON object due to
                    the following error: ${err.message}. Returning simi-transformed object that will most likely
                    have missing properties.`);
        }

        return jsonObject;
    }

    /**
     * @public @function
     * Logs the err and errorMessage base upon one the following:
     *  1) Attempts to log the provided err and errorMessage using EzClocker's EzLogger singleton instance.
     *  2) If the EzClocker EzLogger instance is not available, then attempts to write the login using the globalThis.console logger.
     *  3) If the EzClocker EzLogger instance is not available and then globalThis.console logger is not available, then the logging attempt is ignored.
     * @param {string} errorMessage
     * Default: EzString.EMPTY || err.mesage (if available)
     * @param {undefined|null|Error} err
     * Default: null
     */
    ezLogError(errorMessage = EzString.EMPTY, err = null) {
        if (!EzString.hasText(errorMessage)) {
            if (EzString.hasText(err?.message)) {
                errorMessage = err.message;
            } else {
                // Ignoring, no error to log
                return;
            }
        } else if (EzString.hasText(err?.message)) {
            errorMessage = EzString.em`
                ${errorMessage}
                [Error reported: ${err.message}]`;
        }

        EzObject.isValid(globalThis?.ezApi?.ezclocker.ezLogger)
            ? globalThis.ezApi.ezclocker.ezLogger.error(errorMessage)
            : globalThis?.console?.error(errorMessage, err)
    }

    /**
     * @public @function
     * Logs the err and errorMessage base upon one the following:
     *  1) Attempts to log the provided err and errorMessage using EzClocker's EzLogger singleton instance.
     *  2) If the EzClocker EzLogger instance is not available, then attempts to write the login using the globalThis.console logger.
     *  3) If the EzClocker EzLogger instance is not available and then globalThis.console logger is not available, then the logging attempt is ignored.
     * @param {string} warningMessage
     * Default: EzString.EMPTY
     */
    ezLogWarning(warningMessage = EzString.EMPTY) {
        if (!EzString.hasText(warningMessage)) {
            // Ignoring, no error to log
            return;
        }

        EzObject.isValid(globalThis?.ezApi?.ezclocker.ezLogger)
            ? globalThis.ezApi.ezclocker.ezLogger.warn(warningMessage)
            : globalThis?.console?.warn(warningMessage)
    }
}
